import { useRoute } from '#imports'

import type { ReviewsTagSentiment } from '@backmarket/http-api/src/api-specs-reviews/types/ai-summary'
import type { ReviewTags } from '@backmarket/http-api/src/api-specs-reviews/types/review'
import { tw } from '@backmarket/utils/string/tw'

/**
 * The reviews API returns span offsets in Unicode code points. JavaScript
 * string indices are UTF-16 code units, so supplementary characters (e.g. 😍)
 * must advance the index by 2 in JS while counting as 1 code point in API offsets.
 */
function utf16IndexFromCodePointOffset(
  str: string,
  codePointOffset: number,
): number {
  let utf16Index = 0
  let consumed = 0

  while (consumed < codePointOffset && utf16Index < str.length) {
    const code = str.codePointAt(utf16Index)
    if (code === undefined) {
      break
    }
    utf16Index += code > 0xffff ? 2 : 1
    consumed += 1
  }

  return utf16Index
}

export function useHighlightComment({
  comment,
  tags,
}: {
  comment?: string
  tags?: ReviewTags[]
}): string | undefined {
  if (!comment || !tags || tags.length < 1) return comment
  const route = useRoute()

  const currentFilters = Array.isArray(route.query.tags__in)
    ? route.query.tags__in
    : [route.query.tags__in]

  const allSpans = tags
    .filter(({ tag }) => currentFilters.includes(tag))
    .flatMap((tag) =>
      tag.textSpans.map((textSpans) => ({
        ...textSpans,
        sentiment: tag.sentiment,
      })),
    )

  if (!allSpans || allSpans.length < 1) return comment

  // Sort spans by start position (and by end position if starts are equal)
  allSpans.sort((a, b) => {
    if (a.start !== b.start) {
      return a.start - b.start
    }

    return a.end - b.end
  })

  const sentimentToClass: Record<ReviewsTagSentiment, string> = {
    positive: tw`bg-static-success-low`,
    negative: tw`bg-static-danger-low`,
    neutral: tw`bg-static-success-low`,
  }

  const segments: string[] = []
  let currentPosition = 0

  allSpans.forEach((span) => {
    const startUtf16 = utf16IndexFromCodePointOffset(comment, span.start)
    const endUtf16 = utf16IndexFromCodePointOffset(comment, span.end)

    // If the span is overlapping the previous one, ignore it
    if (startUtf16 < currentPosition) return

    // Add text before the span
    if (startUtf16 > currentPosition) {
      segments.push(comment.substring(currentPosition, startUtf16))
    }

    // Add the wrapped span
    segments.push(
      `<mark class="p-2 mx-[-2px] ${sentimentToClass[span.sentiment]}">${comment.substring(startUtf16, endUtf16)}</mark>`,
    )

    currentPosition = endUtf16
  })

  // Add remaining text after the last span
  if (currentPosition < comment.length) {
    segments.push(comment.substring(currentPosition))
  }

  return segments.join('')
}
